import { environment } from "src/environments/environment";
import { ErrorType, LFService, LogGroupRule, LogLevel, LogMessage, Logger, LoggerFactory, LoggerFactoryOptions, MessageType, LogData } from "typescript-logging";


const LogLevelNames: Record<LogLevel, string> = {
  [LogLevel.Info]: 'INFO',
  [LogLevel.Debug]: 'DEBUG',
  [LogLevel.Error]: 'ERROR',
  [LogLevel.Fatal]: 'FATAL',
  [LogLevel.Trace]: 'TRACE',
  [LogLevel.Warn]: 'WARN'
}

class CustomLogger implements Logger {

  constructor(public name: string, private group: LogGroupRule) {}

  trace(msg: MessageType, error?: ErrorType): void {
    if(this.isTraceEnabled()) {
      console.trace(this.format({ msg, error}))
    }
  }

  debug(msg: MessageType, error?: ErrorType): void {
    if(this.isDebugEnabled()) {
      console.debug(this.format({ msg, error}))
    }
  }

  info(msg: MessageType, error?: ErrorType): void {
    if(this.isInfoEnabled()) {
      console.info(this.format({ msg, error}))
    }
  }

  warn(msg: MessageType, error?: ErrorType): void {
    if(this.isWarnEnabled()) {
      // console.warn(msg, error);
      console.log(this.format({ msg, error}))
    }
  }

  error(msg: MessageType, error?: ErrorType): void {
    if(this.isErrorEnabled()) {
      console.error(this.format({ msg, error}))
    }
  }

  fatal(msg: MessageType, error?: ErrorType): void {
    if(this.isFatalEnabled()) {
      console.error(this.format({ msg, error}))
    }
  }

  isTraceEnabled(): boolean {
    return LogLevel.Trace >= this.getLogLevel() && !environment.production;
  }

  isDebugEnabled(): boolean {
    return LogLevel.Debug >= this.getLogLevel() && !environment.production;
  }

  isInfoEnabled(): boolean {
    return LogLevel.Info >= this.getLogLevel();
  }

  isWarnEnabled(): boolean {
    return LogLevel.Warn >= this.getLogLevel();
  }

  isErrorEnabled(): boolean {
    return LogLevel.Error >= this.getLogLevel();
  }

  isFatalEnabled(): boolean {
    return LogLevel.Fatal >= this.getLogLevel();
  }

  getLogLevel(): LogLevel {
    return this.group.level;
  }

  private format({ msg, error }: { msg: MessageType; error?: ErrorType }) {


    const messageHandled = this.handleMessage(msg);

    const errHandled = error instanceof Error ? error : typeof error === 'function' ? error() : null;

    const finalMessage: LogMessage = {
      loggerName: this.name,
      level: this.getLogLevel(),
      logGroupRule: this.group,
      message: messageHandled,
      isMessageLogData: !!(messageHandled as LogData).data,
      error: errHandled,
      errorAsStack: errHandled instanceof Error ? errHandled.stack : null,
      date: new Date(),
      messageAsString: typeof messageHandled === 'string' ? messageHandled : `${messageHandled.msg} ${messageHandled.ds(messageHandled.data)}`,
      logData: typeof messageHandled !== 'string' ? messageHandled : null
    }

    const formatter = this.group.formatterLogMessage || this.basicFormatter.bind(this);

    return formatter(finalMessage);
  }

  private handleMessage(message: MessageType): string | LogData {
    switch (typeof message) {
      case "string":
        return message;
      case "object":
        const msg: LogData = { data: message.data, msg: message.msg, ds: () => this.dataToString(message.data) };
        return msg;
      case "function":
        return message();
    }
  }

  private dataToString(data?: any) {
    if(!data) {
      return '';
    }

    return JSON.stringify(data);
  }

  private basicFormatter(message: LogMessage) {
    const levelStr = LogLevelNames[this.getLogLevel()];
    return `[${levelStr}] [${this.name}] ${message.messageAsString}`
  }

}



class CustomLoggerFactory implements LoggerFactory {

  private loggers = new Map<string, Logger>();
  // private loggerGroups: Array<LogGroupRule> = []

  constructor(private name: string, private options: LoggerFactoryOptions) {

  }

  configure(options: LoggerFactoryOptions): void {
    this.options = options;
  }

  getLogger(named: string): Logger {

    if(!this.loggers.has(named)) {
      const group = this.options.logGroupRules.find((v) => v.regExp.test(named));
      const logger = new CustomLogger(named, group);
      this.loggers.set(named, logger);
      return logger;
    }

    return this.loggers.get(named);

  }

  isEnabled(): boolean {
    return this.options.enabled;
  }

  getName(): string {
    return this.name;
  }

}

export class CustomLFService extends LFService {

  constructor() {
    super()
  }


  static createNamedLoggerFactory(name: string, options?: LoggerFactoryOptions): LoggerFactory {
      // const factory = super.createNamedLoggerFactory(name, options);
      const factory = new CustomLoggerFactory(name, options);
      return factory;
  }

}


